import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { Button, Form } from 'antd';
import {
  Drawer,
  TextArea,
  Icon,
  ButtonGroup,
  Gutter,
} from '@common/components';
import { createMessage } from 'works/legacy/lib/message-helper';
import { OfferDataTable } from 'works/legacy/components/offer-data-table';
import moment from 'moment';
import { OfferFormDetails } from 'works/legacy/components/offer-form-details';
import { useMutation } from '@common/hooks';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import {
  ChangeRequestDialogBox,
  OfferInfoSummary,
} from '@applicant-flow/components';
import { CreateResponseOfferAcceptance } from 'works/legacy/graphql/change-response';
import { showNotification } from '@common/services';

export function OfferChangeResponseDrawer({
  application,
  offer,
  visible,
  edit,
  offerChangeRequestId,
  onDrawerClose = () => {},
}) {
  const CHANGE_RESPONSE_TEXT_MAX_LENGTH = 160;

  const [form] = Form.useForm();

  const [isFormValid, setIsFormValid] = useState(false);

  const [submitOfferChangeResponse, { loading }] = useMutation(
    CreateResponseOfferAcceptance,
    {
      onCompleted: (response) => {
        if (response.createResponseOfferAcceptance.errors) {
          addErrorsToForm(form, response.createResponseOfferAcceptance.errors);
        } else {
          createMessage('success', 'Change response has been submitted.');
          onClose();
        }
      },
      onError: () => {
        createMessage('error', 'Failed to perform action. Please try again.');
      },
      refetchQueries: ['StaffingRequestPageQuery'],
    }
  );

  const initialValues = {
    startDate: moment(offer.startDate),
    endDate: moment(offer.endDate),
    timeoffDescription: offer.timeoffDescription,
    shiftType: offer.shiftType,
    shiftsPerWeek: offer.shiftsPerWeek,
    hoursPerShift: offer.hoursPerShift,
  };

  function hasFormAnyError(form) {
    const values = form.getFieldsValue();
    const emptyFields = Object.values(values).filter((v) => !v);
    const errors = form.getFieldsError();
    const fieldsWithErrors = errors.filter((f) => f.errors.length > 0);

    const editedFields = Object.entries(initialValues).filter((array) => {
      const [key, value] = array;
      if (key === 'startDate' || key === 'endDate') {
        return !value.isSame(values[key]);
      }
      return value !== values[key];
    });

    return (
      emptyFields.length > 0 ||
      fieldsWithErrors.length > 0 ||
      editedFields.length === 0
    );
  }

  function onChange() {
    setTimeout(() => {
      const hasError = hasFormAnyError(form);
      setIsFormValid(!hasError);
    });
  }

  function onClose() {
    form.resetFields();
    setIsFormValid(false);
    onDrawerClose();
  }

  const lastChangeRequest =
    offer.offerChangeRequests[offer.offerChangeRequests.length - 1];

  const paramChangeRequest = offer.offerChangeRequests.find(
    (cr) => cr.id === offerChangeRequestId
  );

  useEffect(() => {
    if (offerChangeRequestId && !paramChangeRequest) {
      showNotification({
        message:
          'The supplier retracted the change request. No changes are needed.',
        icon: <Icon kind="light" icon="circleInfo" color="elm" />,
        duration: 5,
      });
    }
  }, [paramChangeRequest, offerChangeRequestId]);

  function saveOfferChangeResponse(values) {
    const input = edit
      ? {
          shiftType: values.shiftType,
          hoursPerShift: values.hoursPerShift,
          shiftsPerWeek: values.shiftsPerWeek,
          startDate: moment(values.startDate).format('YYYY-MM-DD'),
          endDate: moment(values.endDate).format('YYYY-MM-DD'),
          timeoffDescription: values.timeoffDescription,
          note: values.offerChangeResponseMessage,
          billingRegularRate: offer.billingRegularRate,
        }
      : {
          shiftType: offer.shiftType,
          hoursPerShift: offer.hoursPerShift,
          shiftsPerWeek: offer.shiftsPerWeek,
          startDate: offer.startDate,
          endDate: offer.endDate,
          timeoffDescription: offer.timeoffDescription,
          note: values.offerChangeResponseMessage,
          billingRegularRate: offer.billingRegularRate,
        };

    submitOfferChangeResponse({
      variables: {
        offerChangeRequestId: lastChangeRequest.id,
        input: input,
      },
    });
  }

  return (
    <Drawer
      className="offer-change-response-drawer"
      open={visible}
      title={edit ? 'Update Offer' : 'Reject Changes'}
      onClose={onClose}
      footer={
        <ButtonGroup>
          <Button
            key="cancelChangeResponseButton"
            size="large"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            key="submitChangeResponseButton"
            size="large"
            disabled={!isFormValid}
            loading={loading}
            onClick={form.submit}
          >
            {edit ? 'Send Updated Offer' : 'Reply'}
          </Button>
        </ButtonGroup>
      }
    >
      {lastChangeRequest && (
        <Gutter direction="bottom" size="md">
          <ChangeRequestDialogBox changeRequest={lastChangeRequest} />
        </Gutter>
      )}
      <Form
        layout="vertical"
        form={form}
        onFinish={saveOfferChangeResponse}
        onChange={onChange}
        initialValues={initialValues}
      >
        <Form.Item
          label="Reply"
          name="offerChangeResponseMessage"
          rules={[
            {
              required: true,
              message: 'Please enter a reply',
            },
            {
              type: 'string',
              min: 1,
              max: CHANGE_RESPONSE_TEXT_MAX_LENGTH,
              message: `Reply must be fewer than ${CHANGE_RESPONSE_TEXT_MAX_LENGTH} characters`,
            },
          ]}
        >
          <TextArea
            name="offerChangeResponseMessage"
            maxLength={CHANGE_RESPONSE_TEXT_MAX_LENGTH}
          />
        </Form.Item>
      </Form>
      <hr />
      {edit ? (
        <>
          <OfferInfoSummary
            application={application}
            rate={offer.billingRegularRate.toFixed(2)}
          />
          <OfferFormDetails
            form={form}
            onFinish={saveOfferChangeResponse}
            onChange={onChange}
            application={application}
            offerType="edit"
            setIsFormValid={setIsFormValid}
          />
        </>
      ) : (
        <OfferDataTable application={application} offer={offer} />
      )}
    </Drawer>
  );
}

OfferChangeResponseDrawer.propTypes = {
  application: propTypes.object,
  offer: propTypes.object,
  visible: propTypes.bool,
  onDrawerClose: propTypes.func,
  edit: propTypes.bool,
  offerChangeRequestId: propTypes.string,
};
